import React, { useContext, useEffect, useMemo } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
} from '@mui/material';

import { useAccess } from '@/common/hooks/useAccess';
import { Button } from '@getethos/ethos-design-system-v2';
import { RootState } from '../../app/rootReducer';
import { AppDispatch } from '../../app/store';
import {
  isUserEligibleForOverviewPage,
  isUserEligibleForQaListPage,
  isUserEligibleForSmartCaseAssignment,
} from '../../common/actionHelper';
import { ApplicationDetailedLifecycle } from '../../common/constants/detailedLifecyle';
import {
  getCarrierRole,
  NoraPermissionV2,
  RoleDetailedLifeCycleMap,
  Roles,
} from '../../common/constants/roles';
import { UserOrganization } from '../../common/constants/userOrganization';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import {
  GetQuickFilters,
  PickRoleForQuickFilters,
} from '../../common/helpers/quickFilters/quickFilters';
import { HttpService } from '../../common/http.service';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';
import { AuthContext } from '../login/AuthWrapper';
import { ReferralCountContext } from '../referral-action-required/ReferralCountContext';
import { MenuItem } from './MenuItem';
import { fetchTaskCounts } from './slices/dashboardV2Slice';
import { taskViewActions } from './slices/statusSlice';
import styles from './styles.module.scss';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

interface SidebarItem {
  name: string;
  path: string;
  visible?: boolean | (() => boolean);
  onClick?: () => void;
}

interface SidebarSection {
  title: string;
  items: SidebarItem[];
}

export const LeftSidebarV2 = ({
  roles,
  userOrganization,
  selectedSectionIndex,
  selectedFilterIndex,
}: {
  roles: string[];
  userOrganization: string;
  selectedSectionIndex: number;
  selectedFilterIndex: number;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const canViewTaskManagement = useAccess({
    permission: NoraPermissionV2.VIEW_TASKS_MANAGEMENT,
  });
  const role = PickRoleForQuickFilters(roles);
  const filters = useMemo(
    () =>
      GetQuickFilters(
        role,
        userOrganization as UserOrganization,
        canViewTaskManagement,
      ),
    [role, userOrganization, canViewTaskManagement],
  );
  const drawerWidth = 240;
  const { taskCounts } = useSelector(state => state.dashboardV2);
  const carrierRole = getCarrierRole(roles);

  const canAssignCarrierCase = useAccess({
    permission: NoraPermissionV2.SMART_CASE_ASSIGNMENT,
    legacyCheck: r =>
      r.includes(Roles.CARRIER_UNDERWRITER) ||
      r.includes(Roles.CHIEF_CARRIER_UNDERWRITER) ||
      r.includes(Roles.LEGAL_CARRIER_UNDERWRITER),
  });

  useEffect(() => {
    dispatch(fetchTaskCounts(filters));
  }, [dispatch, filters]);

  const { openSnack } = useContext(SnackbarContext);
  const canUseSmartCaseAssignment = useAccess({
    permission: 'SMART_CASE_ASSIGNMENT',
    legacyCheck: isUserEligibleForSmartCaseAssignment,
  });

  /*
We aim to allocate applications to the user only if they have supporting evidence.
Any stages that should be bypassed will be provided in the "withoutStages" attribute.
*/
  const onClick = async (
    detailedLifecycles: ApplicationDetailedLifecycle[],
    sorting: string,
    isCarrierAssigned?: boolean,
  ) => {
    const url = `applications/assignment/assign-me`;
    const resp = await HttpService.getInstance().post(url, {
      detailedLifecycles,
      sorting,
      isCarrierAssigned,
    });
    if (resp && resp.parsedBody !== -1) {
      window.open(`/workbench/applications/${resp.parsedBody}`, '_blank');
    } else {
      openSnack({
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message:
          'Either no unassigned application exists, or some error occurred',
      });
    }
  };

  const handleSmartCaseAssignment = async () => {
    const url = `applications/assignment/smart-assign`;
    const resp = await HttpService.getInstance().post(url, {});

    // Backend now returns an array of application IDs
    if (resp && Array.isArray(resp.parsedBody)) {
      const applicationIds = resp.parsedBody;

      // Check if no cases were assigned (returns empty array)
      if (applicationIds.length === 0) {
        openSnack({
          id: uuidv4(),
          type: SnackBarType.ALERT,
          message: 'No unassigned case exists',
        });
      } else if (applicationIds.length > 0) {
        // Open all assigned applications in new tabs
        applicationIds.forEach(appId => {
          window.open(`/workbench/applications/${appId}`, '_blank');
        });
      }
    } else {
      openSnack({
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message: 'Error in assigning case',
      });
    }
  };

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          paddingTop: '72px',
          paddingBottom: '72px',
          zIndex: '0',
        },
      }}
      variant="permanent"
      anchor="left"
    >
      <ApplicationSidebar />
      {canViewTaskManagement &&
        filters?.section.length > 0 &&
        filters.section.map((s, sectionIdx) => (
          <List
            key={sectionIdx}
            subheader={<ListSubheader>{s.sectionName}</ListSubheader>}
          >
            {s?.list?.map((l, filterIdx) => (
              <ListItem
                key={filterIdx}
                disablePadding
                sx={{
                  '.MuiListItemButton-root': {
                    padding: '2px 0px 2px 24px',
                  },
                }}
              >
                <MenuItem
                  sectionIndex={sectionIdx}
                  filterIndex={filterIdx}
                  selectedFilterIndex={selectedFilterIndex}
                  selectedSectionIndex={selectedSectionIndex}
                  quickFilter={l}
                  taskCounts={taskCounts.count}
                />
              </ListItem>
            ))}
          </List>
        ))}
      <div className={styles.sidebarButtonsContainer}>
        {
          <>
            {userOrganization === UserOrganization.ETHOS &&
              canUseSmartCaseAssignment && (
                <Button
                  variant="infoSolid"
                  style={{ fontFamily: 'Theinhardt' }}
                  buttonTitle="Assign Case"
                  onClick={handleSmartCaseAssignment}
                />
              )}
            {carrierRole && canAssignCarrierCase && (
              <Button
                variant="infoSolid"
                style={{
                  fontFamily: 'Theinhardt',
                }}
                buttonTitle="Assign Any Case"
                onClick={() => {
                  onClick(
                    [RoleDetailedLifeCycleMap.get(carrierRole!)!],
                    'ASC',
                    true,
                  );
                }}
              />
            )}
          </>
        }
      </div>
    </Drawer>
  );
};

const ApplicationSidebar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { flags } = useContext(FeatureFlagsContext);
  const isReferralVisible =
    flags.isCaseReferralEnabled &&
    auth.userOrganization === UserOrganization.ETHOS;
  const { count: referralActionRequiredCount } =
    useContext(ReferralCountContext);
  const isPrioritizedView = auth.prioritizedView;
  const overviewMatch = useMatch('/workbench/overview');
  const qaListMatch = useMatch('/workbench/qa');
  const referralsActionRequiredMatch = useMatch(
    '/workbench/referrals/action-required',
  );

  const canViewOverview = useAccess({
    permission: NoraPermissionV2.VIEW_OVERVIEW,
    legacyCheck: isUserEligibleForOverviewPage,
  });

  const canViewQaTab = useAccess({
    permission: NoraPermissionV2.VIEW_QA_CASE,
    legacyCheck: isUserEligibleForQaListPage,
  });

  const canViewReferralActionRequired = useAccess({
    permission: NoraPermissionV2.VIEW_CASE_REFERRAL,
  });

  const lastTaskSectionIndex = useSelector(
    state => state.taskView.lastSectionIndex,
  );
  const lastTaskFilterIndex = useSelector(
    state => state.taskView.lastFilterIndex,
  );

  const APPLICATION_VIEW_INDEX = -1;

  // Memoized functions
  const isApplicationView = useMemo(
    (): boolean =>
      lastTaskSectionIndex === APPLICATION_VIEW_INDEX &&
      lastTaskFilterIndex === APPLICATION_VIEW_INDEX,
    [lastTaskSectionIndex, lastTaskFilterIndex],
  );

  const getIsActive = (item: SidebarItem) => {
    if (!isApplicationView && item.name !== 'Referral Action Required')
      return false;
    switch (item.name) {
      case 'All':
        return (
          !isPrioritizedView &&
          !overviewMatch &&
          !qaListMatch &&
          !referralsActionRequiredMatch
        );
      case 'Prioritized':
        return (
          isPrioritizedView &&
          !overviewMatch &&
          !qaListMatch &&
          !referralsActionRequiredMatch
        );
      case 'Overview':
        return Boolean(overviewMatch);
      case 'QA':
        return Boolean(qaListMatch);
      case 'Referral Action Required':
        return Boolean(referralsActionRequiredMatch);
      default:
        return false;
    }
  };

  // This is used to reset the task view sidebar items
  const handleBackToApplicationView = () => {
    dispatch(
      taskViewActions.onTaskViewChange({
        lastSectionIndex: -1,
        lastFilterIndex: -1,
      }),
    );
  };
  const applicationSection: SidebarSection = {
    title: 'Applications',
    items: [
      {
        name: 'Prioritized',
        path: '/workbench',
        visible: auth.userOrganization === UserOrganization.ETHOS,
        onClick: () => {
          auth.setPrioritizedView?.(true);
          navigate('/workbench/?prioritizedView=true');
          handleBackToApplicationView();
        },
      },
      {
        name: 'All',
        path: '/workbench',
        visible: true,
        onClick: () => {
          auth.setPrioritizedView?.(false);
          navigate('/workbench/?prioritizedView=false');
          handleBackToApplicationView();
        },
      },
      {
        name: 'Overview',
        path: '/workbench/overview',
        visible:
          auth.userOrganization === UserOrganization.ETHOS && canViewOverview,
        onClick: () => {
          navigate('/workbench/overview');
          handleBackToApplicationView();
        },
      },
      {
        name: 'QA',
        path: '/workbench/qa',
        visible:
          flags.isQaWorkflowEnabled &&
          auth.userOrganization === UserOrganization.ETHOS &&
          canViewQaTab,
        onClick: () => {
          navigate('/workbench/qa');
          handleBackToApplicationView();
        },
      },
      {
        name: 'Referral Action Required',
        path: '/workbench/referrals/action-required',
        visible: isReferralVisible && canViewReferralActionRequired,
        onClick: () => {
          navigate('/workbench/referrals/action-required');
          handleBackToApplicationView();
        },
      },
    ],
  };

  return (
    <List
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
      subheader={<ListSubheader>{applicationSection.title}</ListSubheader>}
    >
      {applicationSection.items.map((item, index) => {
        const isVisible =
          typeof item.visible === 'function' ? item.visible() : item.visible;

        if (!isVisible) return null;

        return (
          <ListItem
            key={index}
            disablePadding
            sx={{
              width: '90%',
              margin: '0 auto',
            }}
          >
            <ListItemButton
              sx={{
                justifyContent: 'start',
                width: '100%',
                position: 'relative',
                backgroundColor: getIsActive(item)
                  ? 'primary.light'
                  : 'transparent',
                color: getIsActive(item) ? 'primary.main' : 'text.secondary',
                borderRadius: '6px',
                paddingBlock: '4px',
                '&:hover': {
                  backgroundColor: getIsActive(item)
                    ? 'primary.light'
                    : 'action.hover',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '0',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '50%',
                  width: '2px',
                  backgroundColor: getIsActive(item)
                    ? 'primary.main'
                    : 'transparent',
                  borderTopRightRadius: '5px',
                  borderBottomRightRadius: '5px',
                },
              }}
              onClick={() => {
                item.onClick?.();
              }}
            >
              <ListItemText
                primary={
                  item.name === 'Referral Action Required' &&
                  referralActionRequiredCount > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      <span>{item.name}</span>
                      <Box
                        component="span"
                        sx={{
                          minWidth: 18,
                          height: 18,
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {referralActionRequiredCount}
                      </Box>
                    </Box>
                  ) : (
                    item.name
                  )
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};
